import { Catalogue } from "../types/catalogue"
import { BaseRule, Condition } from "../types/tabSpecDocument"
import {
  conditionExpression,
  conditionRefusal,
  describeConditionGroups,
} from "../model/conditions"
import { ResolvedTable } from "../model/resolve"
import { RAW_NAMES, ShownNames } from "../model/shownLabels"

export interface BasePlan {
  /** The rule the table is run with; it can differ from the one asked for. */
  effective: BaseRule
  /** The CALC expression of the universe; null = no universe. */
  expression: string | null
  /** Fill the rows copy with false, so that nobody drops out for a missing answer. */
  blankFillRows: boolean
  baseText: string
  filterText: string | null
  warnings: string[]
  /**
   * Why the table cannot run: a condition of the book filter or of the
   * table's filter cannot go into the script. A filter decides who is in the
   * table, so it is applied in full or not at all. null = the table can run.
   */
  skipReason: string | null
}

type Token =
  | { kind: "text"; value: string }
  | { kind: "open" }
  | { kind: "close" }
  | { kind: "op"; value: "and" | "or" | "not" }
type Node = { atom: string } | { not: Node } | { parts: (Node | "and" | "or")[] }

const TOKEN = /<<(.*?)>>|(\()|(\))|\b(AND|OR|NOT)\b/g

/** A codebook path in words, without shown labels: "Gate//Yes" gives "Gate: Yes". */
const pathText = (path: string): string => path.split("//").join(": ")

const tokenize = (expression: string, name: (path: string) => string): Token[] => {
  const out: Token[] = []
  let last = 0
  const text = (s: string): void => {
    const t = s.trim()
    if (t) out.push({ kind: "text", value: t })
  }
  for (const m of expression.matchAll(TOKEN)) {
    text(expression.slice(last, m.index))
    last = (m.index ?? 0) + m[0].length
    if (m[1] !== undefined) out.push({ kind: "text", value: name(m[1]) })
    else if (m[2]) out.push({ kind: "open" })
    else if (m[3]) out.push({ kind: "close" })
    else out.push({ kind: "op", value: m[4].toLowerCase() as "and" | "or" | "not" })
  }
  text(expression.slice(last))
  return out
}

const parse = (tokens: Token[]): Node => {
  let i = 0
  const group = (): Node => {
    const parts: (Node | "and" | "or")[] = []
    while (i < tokens.length && tokens[i].kind !== "close") {
      const t = tokens[i]
      if (t.kind === "op" && t.value !== "not") {
        parts.push(t.value)
        i++
      } else parts.push(term())
    }
    return { parts }
  }
  const term = (): Node => {
    const t = tokens[i]
    if (t.kind === "op") {
      i++
      return { not: i < tokens.length ? term() : { atom: "" } }
    }
    if (t.kind === "open") {
      i++
      const inner = group()
      if (i < tokens.length) i++
      return inner
    }
    const words: string[] = []
    while (i < tokens.length && tokens[i].kind === "text")
      words.push((tokens[i++] as { kind: "text"; value: string }).value)
    return { atom: words.join(" ") }
  }
  return group()
}

/** Brackets around a single thing are dropped. */
const simplify = (node: Node): Node => {
  if ("atom" in node) return node
  if ("not" in node) return { not: simplify(node.not) }
  const parts = node.parts.map((p) => (typeof p === "string" ? p : simplify(p)))
  const terms = parts.filter((p): p is Node => typeof p !== "string")
  return terms.length === 1 ? terms[0] : { parts }
}

const print = (node: Node, nested: boolean): string => {
  if ("atom" in node) return node.atom
  if ("not" in node) return `not ${print(node.not, true)}`
  const text = node.parts.map((p) => (typeof p === "string" ? p : print(p, true))).join(" ")
  return nested ? `(${text})` : text
}

/**
 * A routing expression in words: "(<<Gate//Yes>>) AND (<<Reason//Price>>)"
 * gives "Gate: Yes and Reason: Price". Text inside a label is never touched.
 * Never throws: an expression it cannot read comes back as readable as it can be.
 * `name` words each path (`ShownNames.path` for the shown labels); without it
 * the parts are joined with ": ".
 */
export const describeCondition = (expression: string, name?: (path: string) => string): string =>
  print(simplify(parse(tokenize(expression, name ?? pathText))), false)
    .replace(/\s+/g, " ")
    .trim()

const respondents = (count: number): string =>
  count === 1 ? "1 respondent" : `${count} respondents`

const LONGEST_CONDITION = 120

/**
 * `names`: the shown labels the base and filter texts and the sentences use.
 * `book`: the spec's book filter, which every table counts within on top of
 * its own filter; [] = none.
 */
export const compileBase = (
  resolved: ResolvedTable,
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES,
  book: Condition = []
): BasePlan => {
  const warnings: string[] = []
  const q = resolved.question
  const instance = resolved.instance ?? q?.instances[0] ?? null
  const name = names.variable(q?.label ?? resolved.table.question)
  let effective = resolved.base
  let asked: string | null = null

  // Without a questionnaire nobody knows who was asked: the base line
  // ("Base: all answering ...") says what the table uses, so no sentence.
  if (effective === "asked" && catalogue.dataOnly) effective = "answered"
  // A variable of the prepared data has no routing, whatever the server says.
  if (effective === "asked" && resolved.table.source === "data") {
    effective = "answered"
    warnings.push(
      `${name} is not in the questionnaire, so there is no routing to say who was asked. The table uses everyone who answered.`
    )
  }
  if (effective === "asked") {
    if (!catalogue.hasRouting) {
      effective = "answered"
      warnings.push(
        catalogue.routingLoading
          ? "The routing is still being read. The table uses everyone who answered for now."
          : "Base from routing is not available on this server. The table uses everyone who answered."
      )
    } else if (!instance || instance.coverage !== "full") {
      effective = "answered"
      warnings.push("The base could not be worked out from the routing. The table uses everyone who answered.")
    } else if (instance.asked && /["\r\n]/.test(instance.asked)) {
      effective = "answered"
      warnings.push("The routing condition has a character the engine cannot take. The table uses everyone who answered.")
    } else {
      asked = instance.asked
    }
  }

  if (instance && effective !== "all") {
    if (effective === "asked" && instance.offPath > 0)
      warnings.push(
        `${respondents(instance.offPath)} answered although they were not asked. They are outside the base.`
      )
    if (instance.missingOnPath > 0)
      warnings.push(
        `${respondents(instance.missingOnPath)} ${instance.missingOnPath === 1 ? "was" : "were"} asked and gave no answer. They are outside the base.`
      )
  }
  // A filter that cannot be applied in full skips the table (`skipReason`):
  // what is left of it would put another population in the table. The book
  // filter is said first: it skips every table of the spec alike.
  const { filter } = resolved.table
  const skipReason =
    conditionRefusal(book, catalogue, names, "book filter") ??
    conditionRefusal(filter, catalogue, names)

  // The routing, then the table's filter, then the book filter. Each group of
  // a condition carries its own brackets, so a table filter alone gives the
  // universe it always gave.
  const expression =
    [asked ? `(${asked})` : null, conditionExpression(filter), conditionExpression(book)]
      .filter(Boolean)
      .join(" AND ") || null

  const generated = (): string => {
    if (effective === "answered") return `Base: all answering ${name}`
    if (effective === "all" || !asked) return "Base: all respondents"
    const words = describeCondition(asked, names.path)
    return words.length <= LONGEST_CONDITION
      ? `Base: all asked ${name} (${words})`
      : `Base: all asked ${name}`
  }
  // One condition in words, the book filter's groups first: a group of
  // several terms is bracketed once there is more than one group in all.
  const described = describeConditionGroups([...book, ...filter], names, catalogue)

  return {
    effective,
    expression,
    blankFillRows: effective === "all",
    baseText: resolved.table.baseText?.trim() || generated(),
    filterText: described ? `Filter: ${described}` : null,
    warnings,
    skipReason,
  }
}
